import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

export type ObserverLocation = {
  lat: number;
  lng: number;
  accuracyMeters?: number;
};

type PlannerContext = {
  open: boolean;
  satellitesOpen: boolean;
  switchingPanels: boolean;
  openSatellites: () => void;
  closeSatellites: () => void;
  picking: boolean;
  location: ObserverLocation | null;
  openPlanner: () => void;
  closePlanner: () => void;
  startPicking: () => void;
  cancelPicking: () => void;
  chooseLocation: (location: ObserverLocation) => void;
};

const PassPlannerContext = createContext<PlannerContext | null>(null);

export const PassPlannerProvider = ({ children }: { children: ReactNode }) => {
  type Panel = "planner" | "satellites" | null;
  const [panels, setPanels] = useState<{ active: Panel; previous: Panel }>({
    active: null,
    previous: null,
  });
  const panel = panels.active;
  const setPanel = useCallback((next: Panel) => {
    setPanels(({ active }) => ({ active: next, previous: active }));
  }, []);
  const closeSatellites = useCallback(() => {
    setPanels((current) => {
      if (current.active !== "satellites") return current;
      return { active: null, previous: current.active };
    });
  }, []);
  const [picking, setPicking] = useState(false);
  const [location, setLocation] = useState<ObserverLocation | null>(null);
  return (
    <PassPlannerContext.Provider
      value={{
        open: panel === "planner",
        satellitesOpen: panel === "satellites",
        switchingPanels:
          panel !== null &&
          panels.previous !== null &&
          panel !== panels.previous,
        openSatellites: () => {
          setPicking(false);
          setPanel("satellites");
        },
        closeSatellites,
        picking,
        location,
        openPlanner: () => {
          setPicking(false);
          setPanel("planner");
        },
        closePlanner: () => setPanel(null),
        startPicking: () => {
          setPanel(null);
          setPicking(true);
        },
        cancelPicking: () => {
          setPicking(false);
          setPanel("planner");
        },
        chooseLocation: (next) => {
          setLocation(next);
          setPicking(false);
          setPanel("planner");
        },
      }}
    >
      {children}
    </PassPlannerContext.Provider>
  );
};

export const usePassPlanner = () => {
  const context = useContext(PassPlannerContext);
  if (!context) throw new Error("PassPlannerProvider is required");
  return context;
};
